import React, { useEffect, useState } from 'react';
import { Button, Badge, Separator, DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, } from '@cargomatic/ui';
import { CheckIcon, ChevronsUpDown, PlusCircle, X } from 'lucide-react';
import { cn } from 'utils/ui/classes';
import { Combobox } from '@headlessui/react';
import { useCustomers } from 'hooks/customers';
import useDebounce from 'hooks/useDebounce';
import { findUsers } from 'utils/UserWebAPIUtils';
export function DataTableCustomerSearchFilter({ column, title, customerTypes = ['carriers', 'shippers'], className, }) {
    var _a, _b;
    const [query, setQuery] = useState('');
    const debouncedQuery = useDebounce(query, 300);
    const [selectedItems, setSelectedItems] = useState([]);
    const selectedItemsCount = (_a = selectedItems === null || selectedItems === void 0 ? void 0 : selectedItems.length) !== null && _a !== void 0 ? _a : 0;
    const selectedValues = selectedItems.map((item) => item.value);
    const { data: items, isLoading } = useCustomers({
        search: debouncedQuery,
        customerTypes,
    });
    const currentFilters = column.getFilterValue();
    const currentFiltersCount = (_b = currentFilters === null || currentFilters === void 0 ? void 0 : currentFilters.length) !== null && _b !== void 0 ? _b : 0;
    async function getUsers() {
        var _a, _b;
        const usersRes = await findUsers({ _id: currentFilters });
        const users = ((_b = (_a = usersRes === null || usersRes === void 0 ? void 0 : usersRes.entity) === null || _a === void 0 ? void 0 : _a.items) !== null && _b !== void 0 ? _b : []).map((item) => ({
            label: item._type === 'Carrier' ? item.name : item.company_name,
            value: item._id,
        }));
        setSelectedItems(users);
    }
    useEffect(() => {
        if (currentFiltersCount > 0 && selectedItemsCount === 0) {
            getUsers();
        }
        else if (currentFiltersCount === 0) {
            setSelectedItems([]);
        }
    }, [currentFiltersCount, selectedItemsCount]);
    function mapFilterValue(item) {
        return item.value;
    }
    function handleChange(e) {
        setQuery(e.target.value);
    }
    function handleSelect(item) {
        setSelectedItems(item);
        setQuery('');
        column.setFilterValue(item.map(mapFilterValue));
    }
    function handleRemove(item) {
        setSelectedItems((existing) => {
            const updatedItems = existing.filter((p) => p.value !== item.value);
            column.setFilterValue(updatedItems.map(mapFilterValue));
            return updatedItems;
        });
    }
    const options = items || [];
    return (React.createElement(DropdownMenu, { modal: false },
        React.createElement(DropdownMenuTrigger, { asChild: true },
            React.createElement(Button, { variant: "outline", size: "sm", className: "border-dashed flex items-center" },
                React.createElement(PlusCircle, { className: "mr-2 h-4 w-4" }),
                title,
                selectedItems.length > 0 && (React.createElement(React.Fragment, null,
                    React.createElement(Separator, { orientation: "vertical", className: "mx-2 h-4" }),
                    React.createElement(Badge, { className: "rounded-sm px-1 font-normal lg:hidden" }, selectedItemsCount),
                    React.createElement("div", { className: "hidden space-x-1 lg:flex" }, selectedItemsCount > 2 ? (React.createElement(Badge, { className: "rounded-sm px-1 font-normal" },
                        selectedItemsCount,
                        " selected")) : ((selectedItems || [])
                        .filter((option) => selectedValues.includes(option.value))
                        .map((option) => (React.createElement(Badge, { key: option.value, className: "rounded-sm px-1 font-normal" }, option.label))))))))),
        React.createElement(DropdownMenuContent, { className: cn('p-0 overflow-y-auto max-h-80', className), align: "start" },
            React.createElement("div", { className: "px-2 py-1.5" },
                React.createElement(Combobox, { value: selectedItems, onChange: handleSelect, multiple: true },
                    React.createElement("div", { className: "relative" },
                        React.createElement("span", { className: "inline-block w-full" },
                            React.createElement("div", { className: "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50" },
                                React.createElement("span", { className: "flex flex-wrap gap-2" },
                                    selectedItems.map((item) => (React.createElement("span", { key: item.value, className: "flex items-center gap-1 rounded-md bg-gray-50 px-2 py-1 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-700/10" },
                                        React.createElement("span", null, item.label),
                                        React.createElement(X, { className: "w-4 h-4 cursor-pointer", onClick: (e) => {
                                                e.stopPropagation();
                                                e.preventDefault();
                                                handleRemove(item);
                                            } })))),
                                    React.createElement(Combobox.Input, { id: "person-search", className: "border-none p-0 focus:ring-0 text-sm disabled:cursor-not-allowed disabled:opacity-50", "aria-label": "Assignee", placeholder: "Search...", value: query, onChange: handleChange, autoComplete: "off" })),
                                React.createElement("div", { className: "py-1 px-1" },
                                    React.createElement(Combobox.Button, { className: "absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none text-gray-400 h-10" },
                                        React.createElement(ChevronsUpDown, { className: "h-5 w-5 text-gray-400", "aria-hidden": "true" }))))),
                        React.createElement(Combobox.Options, { className: "z-10 mt-1 w-full overflow-auto py-1 focus:outline-none sm:text-sm " },
                            !isLoading && options.length === 0 && (React.createElement("div", { className: "data-[focus]:bg-gray-600 group relative cursor-default select-none py-2 pl-3 pr-9 text-gray-900 data-[focus]:text-white" }, "There are no results.")),
                            options.length > 0 &&
                                options.map((item) => (React.createElement(Combobox.Option, { value: item, key: item.value, className: ({ active }) => {
                                        return cn('relative cursor-default select-none py-2 pl-3 pr-9 text-gray-900 focus:outline-none focus:bg-gray-600 focus:text-white', active ? 'bg-gray-600 text-white' : 'text-gray-900');
                                    } }, ({ active, selected }) => (React.createElement(React.Fragment, null,
                                    React.createElement("span", { className: "block truncate group-data-[selected]:font-semibold" }, item.label),
                                    selected && (React.createElement("span", { className: cn('absolute inset-y-0 right-0 items-center pr-4 flex focus:text-white', active ? 'text-white' : 'text-gray-600') },
                                        React.createElement(CheckIcon, { className: "h-5 w-5", "aria-hidden": "true" })))))))))))))));
}
